import React, { ChangeEvent, useEffect, useState } from 'react';
import './MapBuilder.scss';
import Client from '../../utils/network';
import { useDispatch } from 'react-redux';
import { setMapBackground } from '../../reducers/map/mapReducerActions';
import { setXTiles, setYTiles } from '../../reducers/gridEdit/gridReducerActions';
import { useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import Modal from '../modal/Modal';
import ModalBody from '../modal/ModalBody';
import ModalHeader from '../modal/ModalHeader';
import { Navigate } from 'react-router';
import Button from '../UI/Button';
import useImageSelector from '../useImageSelector';
import Input from '../UI/Input';

const SaveMapModal = ({ show, toggle}: { show: boolean, toggle: () => void}) => {
    const [mapName, setMapName] = useState('');
    const [createdMapId, setCreatedMapId] = useState<string>();

    const { map, grid } = useSelector((state: Rootstate) => ({
        map: state.map,
        grid: state.grid,
    }));

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setMapName(e.currentTarget.value);
    };

    const handleSaveMap = () => {
        Client.request('/api/map/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: mapName,
                backgroundKey: map.map?.background,
                backgroundWidth: grid.xTiles,
                backgroundHeight: grid.yTiles,
            }),
        }).then(async (res) => {
            const body = await res.json() as { _id: string };
            setCreatedMapId(body._id);
            return;
        }).catch(err => {
            // eslint-disable-next-line no-console
            console.log(err);
        });
    };

    return (
        <>
            {createdMapId && (
                <Navigate to={`/main/view/${createdMapId}`} />
            )}
            <Modal toggle={toggle} show={show}>
                <ModalHeader toggle={toggle}>
                    Title
                </ModalHeader>
                <ModalBody>
                    <label htmlFor="mapname">
                        Map name
                        <Input
                            value={mapName}
                            className='ms-1 text-black'
                            id="mapname"
                            onChange={handleNameChange} />
                    </label>
                    <button
                        className='block p-2 m-2'
                        onClick={handleSaveMap}
                    >
                        Save map
                    </button>
                </ModalBody>
            </Modal>
        </>
    );
};

const MapBuilder = () => {
    const [isUploading, setIsUploading] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const dispatch = useDispatch();

    const {
        resetImageSelector,
        imageSelector,
    } = useImageSelector({
        type:'map',
        onSelect: (id: string) => dispatch(setMapBackground(id)),
        className:'maps bg-back-blue rounded-lg pretty-scroll',
    });

    const { xTiles, yTiles } = useSelector((state: Rootstate) => ({
        ...state.grid,
    }));

    const [width, setWidth] = useState(xTiles.toString());
    const [height, setHeight] = useState(yTiles.toString());

    useEffect(() => {
        if(width === '' || parseInt(width) <= 0) {
            dispatch(setXTiles(1));
        } else {
            dispatch(setXTiles(parseInt(width)));
        }
    }, [width]);

    useEffect(() => {
        if(height === '' || parseInt(height) <= 0) {
            dispatch(setYTiles(1));
        } else {
            dispatch(setYTiles(parseInt(height)));
        }
    }, [height]);

    const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        setIsUploading(true);
        if(!e.target.files || e.target.files.length === 0) {
            return;
        }

        const formData = new FormData();
        formData.append('file', e.target.files[0]);
        const res = await Client.request('/api/image/map', {
            method: 'POST',
            body: formData,
        }).catch(() => {
            return;
        }).finally(() => {
            setIsUploading(false);
            resetImageSelector();
        });
        if(!res) {
            return;
        }
        const body = await res.json() as { id: string };
        dispatch(setMapBackground(body.id));
    };


    return (
        <>
            <div className='map-builder bg-deep-blue grow'>
                <div className="p-2 pt-0">
                    {imageSelector}
                </div>
                <div className='pt-3 ps-2 pb-3 flex justify-center overflow-visible'>
                    <label
                        htmlFor="files"
                        className={`block p-3 pt-2 pb-2 w-fit bg-mid-blue rounded-md ${isUploading ? 'disabled' : ''}`}
                    >
                        {isUploading
                            ? 'Uploading new background'
                            : 'Upload new background'}
                        <Input
                            disabled={isUploading}
                            style={{ display: 'none' }}
                            id='files' type="file" accept="image/*" onChange={handleUpload}
                        />
                    </label>
                </div>
                <div className='p-2 '>
                    <div className="bg-back-blue rounded p-2 pb-4">
                        <span>
                            Map size
                        </span>
                        <br />
                        <span>
                            <span>
                                <label className="pl-1">
                                    x
                                </label>
                                <Input value={width} onChange={(e) => setWidth(e.target.value)} />
                            </span>
                            <br />
                            <span>
                                <label className="pl-1">
                                    y
                                </label>
                                <Input
                                    value={height} onChange={(e) => setHeight(e.target.value)} />
                            </span>
                        </span>
                    </div>
                </div>
                <div className='flex justify-center p-2'>
                    <Button onClick={() => setShowSaveModal(true)}>
                        Save map
                    </Button>
                </div>
                <SaveMapModal toggle={() => setShowSaveModal(false)} show={showSaveModal} />
            </div>
        </>
    );
};

export default MapBuilder;
